import Section from '@lyra/core/components/Section'
import { CurrencyId } from '@lyra/web/constants/tokens'
import { MarketContext } from '@lyra/web/providers/MarketProvider'
import { createTradingViewDataFeed } from '@lyra/web/utils/tradingView'
import { memo, useContext, useEffect, useRef, useState } from 'react'
import { Stack, StackProps, useTheme } from 'tamagui'
import { ChartingLibraryWidgetOptions, ResolutionString, widget } from 'tradingview'

type Props = {
  currencyId: CurrencyId
} & StackProps

const TradingViewChart = ({ currencyId, ...stackProps }: Props) => {
  const chartContainerRef = useRef<HTMLElement>(null)
  const [isChartReady, setIsChartReady] = useState(false)

  const getCurrencies = useContext(MarketContext).getCurrencies

  const theme = useTheme()

  const greenColor = theme.green?.val
  const redColor = theme.red?.val
  const whiteColor = theme.primaryText?.val
  const bgColor = theme.appBg?.val

  useEffect(() => {
    if (!chartContainerRef.current) {
      return
    }

    const dataFeed = createTradingViewDataFeed(currencyId, getCurrencies)

    const widgetOptions: ChartingLibraryWidgetOptions = {
      symbol: currencyId,
      datafeed: dataFeed,
      interval: '15' as ResolutionString,
      container: chartContainerRef.current,
      library_path: '/tradingview/',
      locale: 'en',
      autosize: true,
      theme: 'dark',
      disabled_features: [
        'header_symbol_search',
        'symbol_search_hot_key',
        'header_compare',
        'compare_symbol_search_spread_operators',
        'header_saveload',
        'save_shortcut',
        'left_toolbar',
        'header_quick_search',
        'header_settings',
      ],
      toolbar_bg: bgColor,
      custom_css_url: '/tradingview/tradingview.css',
      custom_font_family: '"GT Standard"',
      overrides: {
        'paneProperties.backgroundGradientStartColor': bgColor,
        'paneProperties.backgroundGradientEndColor': bgColor,
        // Bars
        'mainSeriesProperties.barStyle.upColor': greenColor,
        'mainSeriesProperties.barStyle.downColor': redColor,
        // Candles
        'mainSeriesProperties.candleStyle.upColor': greenColor,
        'mainSeriesProperties.candleStyle.downColor': redColor,
        'mainSeriesProperties.candleStyle.borderUpColor': greenColor,
        'mainSeriesProperties.candleStyle.borderDownColor': redColor,
        'mainSeriesProperties.candleStyle.wickUpColor': greenColor,
        'mainSeriesProperties.candleStyle.wickDownColor': redColor,
        'mainSeriesProperties.candleStyle.barColorsOnPrevClose': false,
        // Hallow Candles
        'mainSeriesProperties.hollowCandleStyle.upColor': greenColor,
        'mainSeriesProperties.hollowCandleStyle.wickUpColor': greenColor,
        'mainSeriesProperties.hollowCandleStyle.downColor': redColor,
        'mainSeriesProperties.hollowCandleStyle.wickDownColor': redColor,
        // Line
        'mainSeriesProperties.lineStyle.color': whiteColor,
        // Line With Markers
        'mainSeriesProperties.lineWithMarkersStyle.color': whiteColor,
        // Step Line
        'mainSeriesProperties.steplineStyle.color': whiteColor,
        // Area
        'mainSeriesProperties.areaStyle.linecolor': whiteColor,
        'mainSeriesProperties.areaStyle.color1': whiteColor,
        'mainSeriesProperties.areaStyle.color2': whiteColor,
        // HLC Area
        'mainSeriesProperties.hlcAreaStyle.highLineColor': greenColor,
        'mainSeriesProperties.hlcAreaStyle.lowLineColor': redColor,
        // Baseline
        'mainSeriesProperties.baselineStyle.topLineColor': greenColor,
        'mainSeriesProperties.baselineStyle.bottomLineColor': redColor,
        // Columns
        'mainSeriesProperties.columnStyle.upColor': greenColor,
        'mainSeriesProperties.columnStyle.downColor': redColor,
        // Heikin Ashi
        'mainSeriesProperties.haStyle.upColor': greenColor,
        'mainSeriesProperties.haStyle.downColor': redColor,
        'mainSeriesProperties.haStyle.borderUpColor': greenColor,
        'mainSeriesProperties.haStyle.borderDownColor': redColor,
      },
      loading_screen: {
        backgroundColor: bgColor,
        foregroundColor: redColor,
      },
      time_frames: [
        {
          text: '1w',
          resolution: '1D' as ResolutionString,
          description: '1 Week',
          title: '1W',
        },
        {
          text: '2w',
          resolution: '1D' as ResolutionString,
          description: '2 Weeks',
          title: '2W',
        },
        {
          text: '1m',
          resolution: '1D' as ResolutionString,
          description: '1 Month',
          title: '1M',
        },
      ],
    }

    const tvWidget = new widget(widgetOptions)

    tvWidget.onChartReady(() => {
      tvWidget.setCSSCustomProperty('--tv-color-pane-background', bgColor)
      tvWidget.setCSSCustomProperty('--tv-color-platform-background', bgColor)
      tvWidget.setCSSCustomProperty('--tv-color-toolbar-button-background', bgColor)
      tvWidget.setCSSCustomProperty('--tv-color-popup-background', bgColor)

      if (widgetOptions.overrides) {
        tvWidget.applyOverrides(widgetOptions.overrides)
      }
      setTimeout(() => setIsChartReady(true), 200)
    })

    return () => {
      tvWidget.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyId, getCurrencies])

  return (
    <>
      <Stack {...stackProps} ref={chartContainerRef} opacity={isChartReady ? 1 : 0} />
      {!isChartReady ? (
        <Section.Spinner style={{ position: 'absolute', top: 0, bottom: 0, right: 0, left: 0 }} />
      ) : null}
    </>
  )
}

export default memo(TradingViewChart)
